import { Controller } from '@hotwired/stimulus'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5mb

const IMAGE_CONFIGS = {
  logo_image: { maxWidth: null, maxHeight: 24, blur: 0 },
  profile_image: { maxWidth: 160, maxHeight: 160, blur: 0 },
  banner_image: { maxWidth: 1000, maxHeight: null, blur: 2 },
}

export default class extends Controller {
  static targets = [
    'placeholder',
    'username',
    'website',
    'instagram',
    'facebook',
    'bluesky',
    'threads',
    'mastodon',
    'twitch',
    'twitter',
    'youtube',
    'tiktok',
    'form',
    'behance',
    'dribbble',
    'vsco',
    'saveButton',
    'profilePic',
    'bannerPic',
    'logoPic',
    'iframe',
    'headerIframe',
    'galleryIframe',
    'saveDialog',
    'usernameWarning',
    'sizeToggle',
    'sizeButton',
    'counter',
    'headerFields',
    'galleryContent',
  ]

  static values = { params: { type: Object, default: {} }, iframeUrl: String }

  headerIframeTargetConnected = () => {
    this.updateHeaderIframe()
  }

  updateHeaderIframe = () => {
    if (this.hasHeaderIframeTarget) {
      const element = this.headerIframeTarget

      setTimeout(() => {
        const doc = element.contentDocument
        doc.body.style.height = 'unset'
        doc.documentElement.style.minHeight = 'unset'
        element.style.height = doc.body.scrollHeight + 'px'
      }, 500)
    }
  }

  usernameTargetConnected = (element) => {
    const initialUsername = element.value
    element.addEventListener('input', () => {
      if (element.value !== initialUsername) {
        this.usernameWarningTarget.classList.remove('hidden')
        element.style.outlineColor = 'red'
      }
      else {
        this.usernameWarningTarget.classList.add('hidden')
        element.style.outlineColor = ''
      }
    })
  }

  taglineTargetConnected = (element) => {
    const update = () => {
      const max = element.maxLength
      const current = element.value.length
      this.counterTarget.textContent = `${current}/${max} chars`

      const atLimit = current === max
      element.classList.toggle('border-red-500', atLimit)
      element.classList.toggle('focus:outline-none', atLimit)
      element.classList.toggle('ring-2', atLimit)
      element.classList.toggle('ring-red-500', atLimit)
      this.counterTarget.classList.toggle('text-red-600', atLimit)
      this.counterTarget.classList.toggle('font-semibold', atLimit)
    }

    element.addEventListener('input', update)

    update()
  }

  sizeButtonTargetConnected = (element) => {
    element.addEventListener('click', () => {
      this.sizeToggleTargets.forEach((sizeToggle) => {
        sizeToggle.classList.toggle('max-w-[500px]')
      })

      element.classList.toggle('bg-gray-900')
      element.classList.toggle('ring-2')
      element.classList.toggle('text-white')
      element.classList.toggle('rounded')
      element.classList.toggle('p-0.5')

      this.updateHeaderIframe()
    })
  }

  updatePlaceholder = (event) => {
    const newValue = event.target.value
    this.placeholderTarget.placeholder = `${newValue}.darkroom.com`
    this.placeholderTarget.value = `${newValue}.darkroom.com`
  }

  toggleSocialInput = (event) => {
    const selected = event.currentTarget
    const social = selected.dataset.selected
    const input = this[`${social}Target`]

    Array.from(selected.children).forEach(ele => ele.classList.toggle('hidden'))
    const currentInputValue = input.querySelector('input').value

    if (currentInputValue) {
      input.querySelector('input').value = ''
      this.showSaveButton()
    }

    input.classList.toggle('hidden')
  }

  showSaveButton = () => {
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.remove('hidden')
      this.saveButtonTarget.classList.add('fixed')
      this.saveButtonTarget.classList.add('z-50')
    }
  }

  removeFirstImage = (element) => {
    const firstImage = element.getElementsByTagName('img')[0]
    firstImage.remove()
  }

  generateRemoveInput = (name) => {
    if (!this.formTarget.querySelector(`input[name="${name}"]`)) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = true
      this.formTarget.appendChild(input)
    }
  }

  // TODO: remove this
  updateViewer = (event) => {
    this.paramsValue = Object.assign(this.paramsValue, event.params.url)
    const params = new URLSearchParams(this.paramsValue).toString()

    this.iframeTarget.src = `${this.iframeUrlValue}?${params}`
  }

  updateGalleryViewer = (event) => {
    // show: 'gallery' is forced rather than relied on from event.params.url so this
    // iframe can never lose it - every caller previously had to remember to include
    // it in its own data-design-url-param, and a single control that forgot would
    // drop `show` from the accumulated paramsValue for every update after it.
    this.paramsValue = Object.assign(this.paramsValue, event.params.url, { show: 'gallery' })
    const params = new URLSearchParams(this.paramsValue).toString()

    // iframeUrlValue is read off this.element, but the "design" controller is attached
    // higher up (AdminTemplateComponent's wrapper) than data-design-iframe-url-value
    // (set on the <form>), so it always evaluated to its empty-string default -
    // producing a base-path-less src like "?show=gallery" that the browser resolved
    // against the current admin page instead of /design/iframes. The gallery iframe's
    // own current src is always correctly rooted (set server-side on first load, or by
    // this same logic on every update since), so its path is reused instead.
    const basePath = new URL(this.galleryIframeTarget.src, window.location.href).pathname

    this.galleryIframeTarget.src = `${basePath}?${params}`
  }

  updateFullName = () => {
    const fullName = [this.firstNameTarget.value, this.lastNameTarget.value].filter(Boolean).join(' ')
    this.fullNameTarget.textContent = fullName
  }

  updateSaveDialog = () => {
    this.saveDialogTarget.innerHTML = 'Darkroom+ changes will be skipped'
  }

  updateHeader = () => {
    const url = new URL(this.headerIframeTarget.src)

    this.headerFieldsTargets.forEach((element) => {
      const paramName = element.name.match(/\[(\w+)\]/)[1]
      url.searchParams.set(paramName, element.value)
    })

    this.headerIframeTarget.src = url.toString()
  }

  processUpload = async (event) => {
    const file = event.target.files[0]
    const fieldName = event.target.name.match(/\[(\w+)\]/)[1]

    if (!file) {
      await this.postUpload({ [fieldName]: 'removed' })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      alert('Image must be under 5MB')
      event.target.value = ''
      return
    }

    const config = IMAGE_CONFIGS[fieldName]
    if (!config) return

    const dataUrl = await this.encodeUpload(file, config)
    await this.postUpload({ [fieldName]: dataUrl })

    this.updateHeader()
    this.updateHeaderIframe()
  }

  postUpload = async (payload) => {
    const response = await fetch('/admin/cache_user_images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify(payload),
    })
    return response.json()
  }

  encodeUpload = (file, { maxWidth, maxHeight, blur }) => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let w = img.naturalWidth
        let h = img.naturalHeight

        if (maxWidth && w > maxWidth) { h = h * (maxWidth / w); w = maxWidth }
        if (maxHeight && h > maxHeight) { w = w * (maxHeight / h); h = maxHeight }

        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (blur) ctx.filter = `blur(${blur}px)`
        ctx.drawImage(img, 0, 0, w, h)

        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.src = URL.createObjectURL(file)
    })
  }

  revealRemove = (event) => {
    const wrapper = event.target.closest('.relative')
    wrapper.querySelector('[data-design-target="removeButton"]').classList.remove('hidden')
  }

  removeImage = async (event) => {
    const button = event.target.closest('[data-field]')
    const field = button.dataset.field
    // send "removed" to redis cache
    await this.postUpload({ [field]: 'removed' })
    // hide the button
    button.classList.add('hidden')
    // show save button
    this.showSaveButton()
    // add a hidden field to the form
    this.generateRemoveInput(`remove_${field}`)
    this.updateHeader()
    this.updateHeaderIframe()
  }
}
